import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useEffect } from 'react';

import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { getRelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import { AppOnlyButton } from '@/shared/components/app-guide/AppOnlyButton';
import { PageHeader } from '@/shared/components/PageHeader';
import { ErrorState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useUIStore } from '@/stores/uiStore';

import { AccountDetail } from './components/AccountDetail';
import { AccountList } from './components/AccountList';

/** 상단 1차 범위 배너 — 닫으면 브라우저에 기억한다 */
const ScopeBanner = () => {
  const dismissed = useUIStore((s) => s.scopeBannerDismissed);
  const dismiss = useUIStore((s) => s.dismissScopeBanner);
  if (dismissed) return null;
  return (
    <div className="flex items-center gap-3.5 rounded-xl bg-ink px-6 py-4">
      <div className="flex flex-col gap-[3px]">
        <p className="text-sm leading-[21px] font-medium text-white">
          웹에서는 일부 기능만 이용할 수 있어요
        </p>
        <p className="text-xs leading-[18px] text-[#9a9a9a]">
          거래처 조회 · 처방 등록 · 실적 · 의약품 검색까지 지원합니다. 그 외
          기능은 앱에서 이용해 주세요.
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="ml-auto text-[#9a9a9a] hover:text-white"
        aria-label="배너 닫기"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};

/** 데스크톱(≥1024)에서만 첫 거래중 병의원을 자동 선택한다. 모바일은 목록이 첫 화면이다 */
const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

/**
 * M-04 · M-05 거래처 — 좌측 마스터(372) + 우측 상세.
 * 모바일(<1024)은 목록 → 상세 2단계: 상세가 선택되면 목록을 숨긴다.
 * API: GET /web/business-relationships, GET /web/business-relationships/{relationshipId}
 */
export const AccountsPage = () => {
  const { relationshipId: relationshipIdParam } = useParams({
    strict: false,
  }) as { relationshipId?: string };
  const relationshipId = relationshipIdParam
    ? Number(relationshipIdParam)
    : undefined;
  const hasSelection = !!relationshipId;
  const navigate = useNavigate();
  const list = useBusinessRelationshipList();
  const items = list.data?.items;
  /** 그룹 숫자는 서버 counts를 그대로 쓴다 */
  const counts = list.data?.counts;

  // /accounts로 들어오면 첫 거래중 병의원(없으면 첫 항목)을 선택한다 — 새로고침 · 공유 시 URL로 유지
  useEffect(() => {
    if (hasSelection || !items?.length || !isDesktop()) return;
    const first =
      items.find((i) => getRelationshipGroup(i.statusView) === 'ongoing') ??
      items[0];
    void navigate({
      to: ROUTE_PATHS.ACCOUNT_DETAIL,
      params: { relationshipId: String(first.relationshipId) },
      replace: true,
    });
  }, [hasSelection, items, navigate]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="거래처"
        description={
          counts
            ? `거래 중 ${counts.ongoing}곳 · 신청 중 ${counts.applying}곳`
            : ' '
        }
        actions={
          <>
            <Button variant="secondary" className="w-[168px]" asChild>
              <Link to={ROUTE_PATHS.PERFORMANCE}>전체 처방 · 정산 내역</Link>
            </Button>
            <AppOnlyButton
              feature="accountApply"
              size="lg"
              className="w-[150px]"
            >
              + 거래처 신청
            </AppOnlyButton>
          </>
        }
      />
      <ScopeBanner />
      <div className="flex items-start gap-6">
        <section
          className={cn(
            'w-full shrink-0 rounded-card border border-line bg-surface p-4 lg:block lg:w-[372px]',
            hasSelection && 'hidden',
          )}
        >
          {list.isLoading && (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-[42px] rounded-md" />
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-[51px] rounded-lg" />
              ))}
            </div>
          )}
          {list.isError && <ErrorState onRetry={() => list.refetch()} />}
          {items && <AccountList items={items} selectedId={relationshipId} />}
        </section>
        <section
          className={cn(
            'min-w-0 flex-1 flex-col lg:flex',
            hasSelection ? 'flex' : 'hidden',
          )}
        >
          {relationshipId && (
            // 거래처를 바꾸면 드로어 · 오버레이 상태를 새로 시작한다
            <AccountDetail
              key={relationshipId}
              relationshipId={relationshipId}
            />
          )}
        </section>
      </div>
    </div>
  );
};
